import { Box, RotateCw } from "lucide-react";
import { cn } from "../lib/cn";

/** Which 3D model an imported piece shows in the walk-through. */
export type ProductModelState = "official" | "generated" | "building" | "failed" | "none";

const COPY: Record<ProductModelState, string> = {
  official: "3D model from the listing.",
  generated: "3D model built from the photos.",
  building: "Building a 3D model from the photos…",
  failed: "Simplified model. The photos couldn't be turned into a 3D model.",
  none: "Simplified model.",
};

/**
 * One line about the piece's 3D model, with "Turn model" once there is a
 * mesh (a quarter turn about the vertical axis, for a mesh whose front
 * faces the wrong way).
 */
export function ProductModelStatus({
  state,
  yaw = 0,
  onTurn,
  tone = "light",
  className,
}: {
  state: ProductModelState;
  /** The mesh's extra turn, degrees. */
  yaw?: number;
  onTurn?: () => void;
  tone?: "light" | "dark";
  className?: string;
}) {
  const dark = tone === "dark";
  const hasMesh = state === "official" || state === "generated";
  return (
    <div
      className={cn(
        "flex items-center gap-2 text-caption",
        dark ? "text-ink-300" : "text-ink-500",
        className
      )}
      data-testid="product-model-status"
      data-state={state}
    >
      {state === "building" ? (
        <span
          className={cn(
            "inline-block size-3.5 shrink-0 rounded-full border-2 animate-spin",
            dark ? "border-white/25 border-t-white" : "border-ink-200 border-t-ink-600"
          )}
          aria-hidden
        />
      ) : (
        <Box className="size-3.5 shrink-0" aria-hidden />
      )}
      <span className="min-w-0 flex-1 leading-snug" role="status">
        {COPY[state]}
        {hasMesh && yaw !== 0 && (
          <>
            {" "}
            <span className="whitespace-nowrap text-ink-400">Turned {yaw}°.</span>
          </>
        )}
      </span>
      {hasMesh && onTurn && (
        <button
          type="button"
          onClick={onTurn}
          data-testid="product-model-turn"
          className={cn(
            "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 font-medium transition-colors",
            dark
              ? "bg-white/10 text-white hover:bg-white/20"
              : "bg-ink-100 text-ink-700 hover:bg-ink-200"
          )}
        >
          <RotateCw className="size-3" aria-hidden />
          Turn model
        </button>
      )}
    </div>
  );
}
